import global from 'global';
import { trimEnd, isArray, get, attempt } from 'lodash';
import defer from 'defer-promise';

import I18n from './i18n';
import Api from './Api';
import Configs, { ConfigLoader } from './Configs';
import Utils from './Utils';
import Bridge from './Bridge';
import WebBridge from './WebBridge';
import UI, { FontsPreset, ColorsPreset } from './UI';
import Dom from './Dom';
import Auth, { BridgeProvider } from './Auth';
import Events from './Events';

let isSDKReady = false;
const deferredInit = defer();

deferredInit.promise.then(() => {
  isSDKReady = true;
});

const wait = (handler) => {
  if (handler) {
    deferredInit.promise.then(handler);
  }

  return deferredInit.promise;
};

async function init(options) {
  const {
    module,
    baseUrl = get(global, 'location.origin', '/'),
    api = {},
    i18n = {},
    configs = {
      load: [],
    },
    ui = {
      autoApply: true,
      presets: {},
    },
    bridge = {
      mode: 'slave',
    },
    auth = {
      provider: new BridgeProvider(Bridge),
    },
  } = options;

  if (!module) {
    throw new Error('`options.module` is required!');
  }

  const deferred = [];
  const normalizedBaseUrl = trimEnd(baseUrl, '/ ');
  const frameworkUrl = `${normalizedBaseUrl}/sat-module-framework`;

  await Dom.init();

  await Configs.init({
    ...configs,
    loader: new ConfigLoader(
      Api,
      configs.endpoint || '/api/configs',
    ),

    // Add default config url
    url: [
      `${frameworkUrl}/config/configs.json`,
      ...(isArray(configs.url) ? configs.url : [configs.url]),
    ],
  });

  /**
   * Add deferred config loader if user is logged in
   */
  Events.on(Events.types.onLogin, () => deferred.push(
    () => Configs.load(configs.load || [])),
  );

  await Bridge.init({
    mockUrl: `${normalizedBaseUrl}/sat-modules/js/tulipNativeBridgeMock.js`,
    ...bridge,
  });

  await Auth.init(auth);
  await Api.init({
    baseUrl,
    ...api,
  });

  await UI.init({
    autoApply: ui.autoApply,
    presets: {
      [UI.COLORS]: ColorsPreset(`${normalizedBaseUrl}/sat-modules/css/colors.css`),
      [UI.FONTS]: FontsPreset(`${normalizedBaseUrl}/sat-modules/css/fonts.css`),

      ...ui.presets,
    },
  });

  await I18n.init({
    backend: {
      crossDomain: true,
      allowMultiLoading: true,
      loadPath: `${frameworkUrl}/locales/?lng={{lng}}&ns={{ns}}`,
    },
    ns: [module],
    defaultNS: module,
    ...i18n,
  });

  await Utils.init();

  /**
   * Wait deferred handlers
   */
  await Promise.all(deferred.map(attempt));

  deferredInit.resolve(this);

  /**
   * Emit SDK ready event
   */
  Events.emit(Events.types.onReady);

  return this;
}

const SDK = {
  wait,
  get isReady() {
    return isSDKReady;
  },

  // Modules
  I18n,
  Api,
  Configs,
  Utils,
  WebBridge,
  Bridge,
  UI,
  Dom,
  Auth,
  Events,
};

SDK.init = SDK::init;

export default SDK;



// WEBPACK FOOTER //
// ./src/sdk.js